import React, { useEffect, useMemo, useRef, useState } from 'react';

const inputClass =
  'w-full px-4 py-3 bg-white border border-amber-100/40 rounded-xl text-primary-dark placeholder-slate-400 text-base md:text-sm font-medium outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all';

const labelFor = (p) =>
  `${p.situs_address || 'Address unavailable'}${
    p.block || p.lot ? ` — Block ${p.block || '-'} / Lot ${p.lot || '-'}` : ''
  }`;

const searchTextFor = (p) =>
  `${labelFor(p)} ${p.owner_name_legal || ''}`.toLowerCase();

const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/**
 * Property picker. Desktop: typeahead (max 4 visible rows, scroll for rest, Tab confirms
 * the highlighted match). Mobile: native <select>. Value is the property id.
 */
export function PropertyCombobox({ properties, value, onChange, disabled, testId }) {
  const [useNative, setUseNative] = useState(true);
  useEffect(() => { setUseNative(isCoarsePointer()); }, []);

  const selected = useMemo(
    () => properties.find((p) => p.id === value) || null,
    [properties, value]
  );

  if (useNative) {
    return (
      <select
        data-testid={testId}
        className={inputClass}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required
      >
        <option value="">Select the affected property</option>
        {properties.map((p) => (
          <option key={p.id} value={p.id}>{labelFor(p)}</option>
        ))}
      </select>
    );
  }

  return <DesktopTypeahead
    properties={properties} value={value} selected={selected}
    onChange={onChange} disabled={disabled} testId={testId}
  />;
}

function DesktopTypeahead({ properties, value, selected, onChange, disabled, testId }) {
  const [query, setQuery] = useState(selected ? labelFor(selected) : '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => { setQuery(selected ? labelFor(selected) : ''); }, [selected]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || (selected && q === labelFor(selected).toLowerCase())) return properties;
    return properties.filter((p) => searchTextFor(p).includes(q));
  }, [properties, query, selected]);

  const commit = (p) => {
    if (!p) return;
    onChange(p.id);
    setQuery(labelFor(p));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && filtered[highlight]) { e.preventDefault(); commit(filtered[highlight]); } }
    else if (e.key === 'Tab') { if (open && filtered[highlight]) commit(filtered[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        data-testid={testId}
        type="text"
        className={inputClass}
        value={query}
        disabled={disabled}
        placeholder="Type an address, owner, block, or lot..."
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${testId || 'property'}-listbox`}
        aria-activedescendant={open && filtered[highlight] ? `${testId || 'property'}-option-${highlight}` : undefined}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); if (!e.target.value) onChange(''); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && filtered.length > 0 && (
        <ul
          id={`${testId || 'property'}-listbox`}
          className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-amber-100/60 warm-tactile-shadow overflow-y-auto"
          style={{ maxHeight: '13.5rem' }}
          role="listbox"
        >
          {filtered.map((p, i) => (
            <li
              key={p.id}
              id={`${testId || 'property'}-option-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => { e.preventDefault(); commit(p); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-4 py-3 text-sm font-medium cursor-pointer ${i === highlight ? 'bg-section-bg text-primary-dark' : 'text-primary-dark hover:bg-section-bg/60'}`}
            >
              {labelFor(p)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PropertyCombobox;
